import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { User } from 'store/auth/models';
import { MappedReturn } from 'store/returns/models/returnData';

/** Stable id for the open row menu. One return can share an entity with others. */
export const submissionMenuKey = (record: MappedReturn) =>
  `${record.connectionId ?? ''}:${record.mappingId ?? ''}:${
    record.dataLog?.uuid ?? ''
  }`;

/**
 * Latest column renderers. Assigned on each submissions render so a memoised
 * cell can call the current closure without rebuilding the column list.
 */
export const submissionsCellApi = {
  renderTaxReturn: (_content: string): React.ReactNode => null,
  renderOrgLink: (
    _content: string,
    _record: MappedReturn
  ): React.ReactNode => null,
  renderPeriod: (_record: MappedReturn): React.ReactNode => null,
  renderDueDate: (
    _content: string,
    _record: MappedReturn
  ): React.ReactNode => null,
  renderPreparer: (_record: MappedReturn): React.ReactNode => null,
  renderReviewer: (_record: MappedReturn): React.ReactNode => null,
  renderClient: (_record: MappedReturn): React.ReactNode => null,
  renderStatus: (_content: string): React.ReactNode => null,
  renderAction: (_record: MappedReturn): React.ReactNode => null,
  renderMenu: (_record: MappedReturn): React.ReactNode => null,
};

export type SubmissionsTableContextValue = {
  currentUser?: User;
  hasAdvancedUserRights: boolean;
  gettingTaxes: boolean;
  isClientLocked: boolean;
};

export const SubmissionsTableContext =
  React.createContext<SubmissionsTableContextValue | null>(null);

/** Which row menu is open. Only the menu cell subscribes. */
export const SubmissionsMenuContext = React.createContext<{
  openMenuKey: string | null;
} | null>(null);

export const SubmissionsTaxReturnCell = ({
  content,
}: {
  content?: string;
}) => {
  useTranslation();
  return submissionsCellApi.renderTaxReturn(content ?? '');
};

export const SubmissionsOrgCell = ({
  content,
  record,
}: {
  content: string;
  record: MappedReturn;
}) => {
  useTranslation();
  return submissionsCellApi.renderOrgLink(content, record);
};

export const SubmissionsPeriodCell = ({ record }: { record: MappedReturn }) => {
  useTranslation();
  return submissionsCellApi.renderPeriod(record);
};

export const SubmissionsDueDateCell = ({
  content,
  record,
}: {
  content?: string;
  record: MappedReturn;
}) => {
  useTranslation();
  return submissionsCellApi.renderDueDate(content ?? '', record);
};

export const SubmissionsPreparerCell = ({
  record,
}: {
  record: MappedReturn;
}) => {
  useContext(SubmissionsTableContext);
  return submissionsCellApi.renderPreparer(record);
};

export const SubmissionsReviewerCell = ({
  record,
}: {
  record: MappedReturn;
}) => {
  useContext(SubmissionsTableContext);
  return submissionsCellApi.renderReviewer(record);
};

export const SubmissionsClientCell = ({
  record,
}: {
  record: MappedReturn;
}) => {
  useContext(SubmissionsTableContext);
  return submissionsCellApi.renderClient(record);
};

export const SubmissionsStatusCell = ({ content }: { content?: string }) => {
  useTranslation();
  return submissionsCellApi.renderStatus(content ?? '');
};

export const SubmissionsActionCell = ({ record }: { record: MappedReturn }) => {
  useTranslation();
  useContext(SubmissionsTableContext);
  return submissionsCellApi.renderAction(record);
};

export const SubmissionsMenuCell = ({ record }: { record: MappedReturn }) => {
  useTranslation();
  useContext(SubmissionsTableContext);
  useContext(SubmissionsMenuContext);
  return submissionsCellApi.renderMenu(record);
};
